import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import config from '../config';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AdminStatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${config.api.url}admin/stats?format=json`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setStats(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const StatCard = ({ title, value, subtitle, icon, color = "primary" }) => (
    <Col md={6} lg={4} className="mb-4">
      <Card className="h-100 shadow-sm">
        <Card.Body>
          <div className="d-flex align-items-center">
            <div className={`text-${color} me-3`} style={{ fontSize: '2rem' }}>
              {icon}
            </div>
            <div>
              <Card.Title className="mb-1">{title}</Card.Title>
              <h3 className={`text-${color} mb-0`}>{value}</h3>
              {subtitle && <small className="text-muted">{subtitle}</small>}
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );

  // Prepare chart data for user signups
  const prepareSignupChartData = () => {
    if (!stats?.users?.timeseries) return null;
    
    const labels = stats.users.timeseries.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    
    const data = stats.users.timeseries.map(item => item.count);
    
    return {
      labels,
      datasets: [
        {
          label: 'User Signups',
          data,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1,
        },
      ],
    };
  };

  // Prepare chart data for submissions
  const prepareSubmissionsChartData = () => {
    if (!stats?.submissions?.timeseries) return null;
    
    const labels = stats.submissions.timeseries.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    
    const data = stats.submissions.timeseries.map(item => item.count);
    
    return {
      labels,
      datasets: [
        {
          label: 'Submissions',
          data,
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          tension: 0.1,
        },
      ],
    };
  };

  // Prepare chart data for unique visitors
  const prepareVisitorChartData = () => {
    if (!stats?.visitors?.timeseries) return null;
    
    const labels = stats.visitors.timeseries.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    
    const data = stats.visitors.timeseries.map(item => item.count);
    
    return {
      labels,
      datasets: [
        {
          label: 'Unique Visitors',
          data,
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading platform statistics...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Statistics</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={fetchStats}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  const signupChartData = prepareSignupChartData();
  const submissionsChartData = prepareSubmissionsChartData();
  const visitorChartData = prepareVisitorChartData();

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-1">📊 Platform Statistics</h1>
          <p className="text-muted mb-0">
            Real-time analytics for Quantum City Challenge
          </p>
        </div>
        <Button variant="outline-primary" onClick={fetchStats} disabled={loading}>
          🔄 Refresh
        </Button>
      </div>

      {/* Quick Stats Cards */}
      <Row>
        <StatCard
          title="Total Users"
          value={stats?.users?.total || 0}
          subtitle={`${stats?.users?.confirmed || 0} confirmed`}
          icon="👥"
          color="primary"
        />
        <StatCard
          title="Unique Visitors"
          value={stats?.visitors?.total || 0}
          subtitle={`${stats?.visitors?.recent || 0} recent`}
          icon="👁️"
          color="info"
        />
        <StatCard
          title="Submissions"
          value={stats?.submissions?.total || 0}
          subtitle={`${stats?.submissions?.recent || 0} recent`}
          icon="📝"
          color="success"
        />
        <StatCard
          title="Questions"
          value={stats?.questions?.total || 0}
          subtitle={`${stats?.questions?.recent || 0} recent`}
          icon="❓"
          color="warning"
        />
        <StatCard
          title="Reviews"
          value={stats?.reviews?.total || 0}
          subtitle={`Avg: ${stats?.reviews?.average_score || 0}`}
          icon="⭐"
          color="secondary"
        />
        <StatCard
          title="Content Pages"
          value={stats?.content?.total_pages || 0}
          subtitle={`${stats?.content?.total_blocks || 0} blocks`}
          icon="📄"
          color="dark"
        />
      </Row>

      {/* Time-Series Charts */}
      <Row className="mt-4">
        <Col lg={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">👁️ Unique Visitors (Last 30 Days)</h5>
            </Card.Header>
            <Card.Body>
              {visitorChartData ? (
                <Line data={visitorChartData} options={chartOptions} />
              ) : (
                <div className="text-center text-muted py-4">
                  <p>No visitor data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">📈 User Signups (Last 30 Days)</h5>
            </Card.Header>
            <Card.Body>
              {signupChartData ? (
                <Line data={signupChartData} options={chartOptions} />
              ) : (
                <div className="text-center text-muted py-4">
                  <p>No signup data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">📊 Submissions (Last 30 Days)</h5>
            </Card.Header>
            <Card.Body>
              {submissionsChartData ? (
                <Line data={submissionsChartData} options={chartOptions} />
              ) : (
                <div className="text-center text-muted py-4">
                  <p>No submission data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Detailed Breakdown */}
      <Row className="mt-4">
        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">👥 User Breakdown</h5>
            </Card.Header>
            <Card.Body>
              {stats?.users ? (
                <div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Total Users:</span>
                    <strong>{stats.users.total}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Confirmed:</span>
                    <span className="text-success">{stats.users.confirmed}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Unconfirmed:</span>
                    <span className="text-warning">{stats.users.unconfirmed}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Recent Signups (7d):</span>
                    <span className="text-info">{stats.users.recent_signups}</span>
                  </div>
                </div>
              ) : (
                <p className="text-muted">No user data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">📊 Activity Summary</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <span>Recent Submissions:</span>
                <strong>{stats?.submissions?.recent || 0}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Recent Questions:</span>
                <strong>{stats?.questions?.recent || 0}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Recent Reviews:</span>
                <strong>{stats?.reviews?.recent || 0}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Average Review Score:</span>
                <strong>{stats?.reviews?.average_score || 0}</strong>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Admin Actions */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">🔧 Admin Actions</h5>
        </Card.Header>
        <Card.Body>
          <div className="d-flex gap-2 flex-wrap">
            <Button 
              variant="outline-primary" 
              href={`${config.api.url}admin`}
              target="_blank"
              rel="noopener noreferrer"
            >
              📋 Full Admin Dashboard
            </Button>
            <Button 
              variant="outline-secondary"
              href={`${config.api.url}admin/stats`}
              target="_blank"
              rel="noopener noreferrer"
            >
              📊 Detailed Stats View
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Footer */}
      <div className="text-center mt-4 pt-3 border-top">
        <small className="text-muted">
          Last updated: {lastUpdated ? lastUpdated.toLocaleString() : 'Never'}
          {' • '}
          <Button variant="link" size="sm" onClick={fetchStats} className="p-0">
            Refresh data
          </Button>
        </small>
      </div>
    </Container>
  );
};

export default AdminStatsPage;
